import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Eye, Shield, AlertTriangle, Check } from 'lucide-react';
import { useUserData } from './UserDataContext';
import { SafeStorage } from '../utils/safeStorage';
import { acceptedRecently, stampAccepted } from '../utils/guidelinesGate';

// Bumped to _v2 alongside the move from sessionStorage to localStorage: the old
// key holds the string "true", not a timestamp, and a fresh name keeps the two
// shapes from ever meeting.
const GUIDELINE_STORAGE_KEY = 'watcher_guidelines_ack_v2_';

const GUIDELINES = [
  {
    icon: '👁️',
    title: 'Observe & Report Only',
    body: 'Your role is to monitor and if you participate or create posts, manage groups, do not bypass any standard user dynamics.',
  },
  {
    icon: '📋',
    title: 'Soft-Deletes Only',
    body: 'Never hard-delete content. All removals must set is_hidden: true so data is preserved for appeal and review.',
  },
  {
    icon: '✍️',
    title: 'Justify Every Action',
    body: 'Every mute, unmute, and deletion requires a written reason. Unjustified actions are a violation of Watcher protocol.',
  },
  {
    icon: '📒',
    title: 'The Ledger is Immutable',
    body: 'All your actions are permanently logged in the Watcher Audit Feed and are visible to all Watchers. Act accordingly.',
  },
  {
    icon: '⚖️',
    title: 'Proportional Response',
    body: 'Use the minimum force necessary. Prefer short mutes. Reserve permanent bans for severe, repeat offenders. ALWAYS refer to the Community Guidelines when making a decision.',
  },
];

export function WatcherGuidelinesModal() {
  const { userProfile, firebaseUser } = useUserData();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!firebaseUser?.uid || userProfile?.rank !== 'watcher') return;

    // At most once a day, not once per session — a relaunched app used to count
    // as a fresh session and re-brief the Watcher all over again.
    if (!acceptedRecently(GUIDELINE_STORAGE_KEY, firebaseUser.uid)) {
      setVisible(true);
    }
  }, [firebaseUser?.uid, userProfile?.rank]);

  const handleAccept = () => {
    if (firebaseUser?.uid) {
      stampAccepted(GUIDELINE_STORAGE_KEY, firebaseUser.uid);
    }
    setVisible(false);
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {visible && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-9999998"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 z-9999999 flex items-center justify-center p-6"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <div
              className="w-full max-w-md rounded-3xl overflow-hidden flex flex-col relative shadow-[0_0_60px_rgba(220,38,38,0.5)] z-50"
              style={{ background: '#000000', border: '1.5px solid #DC2626', maxHeight: '85vh', transform: 'translateZ(0)' }}
            >
              {/* Header */}
              <div className="px-6 pt-8 pb-5 text-center shrink-0 bg-black relative z-30" style={{ borderBottom: '1px solid rgba(220,38,38,0.25)' }}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.4)' }}>
                  <Eye className="w-8 h-8" style={{ color: '#DC2626' }} />
                </div>
                <p className="text-xs uppercase tracking-[0.25em] font-bold mb-1" style={{ color: '#DC2626' }}>
                  Watcher Protocol
                </p>
                <h2 className="text-2xl font-black text-white">Eyes &amp; Ears Guidelines</h2>
                <p className="text-sm mt-2 text-white/50 px-4">Read before you proceed — required every session.</p>
              </div>

              {/* Guidelines */}
              <div className="overflow-y-auto w-full relative z-20 bg-black block">
                <div className="px-6 py-8 space-y-10">
                  {GUIDELINES.map((g, i) => (
                    <div key={i} className="flex gap-4 items-start relative bg-black mb-6">
                    <span className="text-2xl mt-0.5 shrink-0" style={{ textShadow: '0 0 10px rgba(255,255,255,0.1)' }}>{g.icon}</span>
                    <div>
                      <p className="text-white font-bold text-base leading-tight mb-1">{g.title}</p>
                      <p className="text-white/60 text-sm leading-relaxed">{g.body}</p>
                    </div>
                  </div>
                ))}
                </div>
              </div>

              {/* Accept Button */}
              <div className="px-6 pb-8 pt-4 shrink-0 bg-black relative z-30" style={{ borderTop: '1px solid rgba(220,38,38,0.1)' }}>
                <button
                  onClick={handleAccept}
                  className="w-full py-4 rounded-2xl text-white font-black text-base flex items-center justify-center gap-2 transition-all active:scale-95"
                  style={{ background: '#DC2626', boxShadow: '0 0 24px rgba(220,38,38,0.5)' }}
                >
                  <Check className="w-5 h-5" />
                  I Understand — Enter
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
